// Ficha del producto y escrituras del catálogo; cada escritura refresca la lista, la ficha y el catálogo.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    inventoryWriteApi,
    type BackofficeCreateKitRequest,
    type BackofficeCreateProductRequest,
    type BackofficeCreateServiceRequest,
    type BackofficeCreateSkuPriceRequest,
    type BackofficeUpdateProductRequest,
} from '@/api/services/retail-catalog/backoffice';
import { getProduct, listProducts } from '@/api/services/retail-catalog/retail-catalog.client';
import { INVENTORY_QUERY_KEY } from './useInventarioLens';
import { retryUnlessClientError } from './useInventorySku';

/** Llave de la ficha completa del producto (lo que la fila del lente no trae). */
export function catalogProductKey(retailerId: string | null, sku: string) {
    return [...INVENTORY_QUERY_KEY, 'product', retailerId, sku] as const;
}

/** Producto completo por SKU, para ver y editar la pestaña Producto. */
export function useCatalogProduct(retailerId: string | null, sku: string, enabled: boolean) {
    return useQuery({
        queryKey: catalogProductKey(retailerId, sku),
        queryFn: () => getProduct(retailerId ?? '', sku),
        enabled: enabled && retailerId !== null,
        retry: retryUnlessClientError,
        staleTime: 60_000,
    });
}

/** Productos de la cadena, para elegir los componentes de un kit. */
export function useCatalogProducts(retailerId: string | null, enabled: boolean) {
    return useQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'products', retailerId],
        queryFn: () => listProducts(retailerId ?? ''),
        enabled: enabled && retailerId !== null,
        retry: retryUnlessClientError,
        staleTime: 60_000,
    });
}

function useRefreshCatalog() {
    const queryClient = useQueryClient();
    return () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: INVENTORY_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: ['retail', 'products'] }),
        ]);
}

export type NewCatalogItem =
    | { kind: 'PRODUCT'; body: BackofficeCreateProductRequest }
    | { kind: 'KIT'; body: BackofficeCreateKitRequest }
    | { kind: 'SERVICE'; body: BackofficeCreateServiceRequest };

/** Alta de producto, kit o servicio. */
export function useCreateCatalogItem() {
    const refresh = useRefreshCatalog();
    return useMutation({
        mutationFn: ({ item, key }: { item: NewCatalogItem; key: string }) => {
            const options = { idempotencyKey: key };
            if (item.kind === 'KIT') return inventoryWriteApi.createKit(item.body, options);
            if (item.kind === 'SERVICE') return inventoryWriteApi.createService(item.body, options);
            return inventoryWriteApi.createProduct(item.body, options);
        },
        onSuccess: refresh,
    });
}

export function useUpdateProduct(sku: string) {
    const refresh = useRefreshCatalog();
    return useMutation({
        mutationFn: ({ body, key }: { body: BackofficeUpdateProductRequest; key: string }) =>
            inventoryWriteApi.updateProduct(sku, body, { idempotencyKey: key }),
        onSuccess: refresh,
    });
}

export function useDeactivateProduct(sku: string) {
    const refresh = useRefreshCatalog();
    return useMutation({
        mutationFn: ({ key }: { key: string }) =>
            inventoryWriteApi.deactivateProduct(sku, { idempotencyKey: key }),
        onSuccess: refresh,
    });
}

export function useCreateSkuPrice(sku: string) {
    const refresh = useRefreshCatalog();
    return useMutation({
        mutationFn: ({ body, key }: { body: BackofficeCreateSkuPriceRequest; key: string }) =>
            inventoryWriteApi.createSkuPrice(sku, body, { idempotencyKey: key }),
        onSuccess: refresh,
    });
}

/** Precio de un SKU que se elige al mandar: el alta registra el de contado sobre el SKU recién creado. */
export function useRegisterSkuPrice() {
    const refresh = useRefreshCatalog();
    return useMutation({
        mutationFn: ({
            sku,
            body,
            key,
        }: {
            sku: string;
            body: BackofficeCreateSkuPriceRequest;
            key: string;
        }) => inventoryWriteApi.createSkuPrice(sku, body, { idempotencyKey: key }),
        onSuccess: refresh,
    });
}
